import React from "react";
import { Truck, PackageCheck, Clock, ShieldCheck, FileText, Download } from "lucide-react";
import Seo from "@/components/Seo";
import { Eyebrow, SectionHeading } from "@/components/ui-bits";
import { COMPANY } from "@/data/site";

const ORDER_FLOW = [
  { title: "Customer orders at eibattery.com.au" },
  { title: "Freight added at checkout", planned: true },
  { title: "Card payment via Stripe + EI order number" },
  { title: "Paid order copied to back-office Shopify" },
  { title: "Gem receives order through integration" },
  { title: "Gem books carrier", planned: true },
  { title: "Dyness warehouse picks, packs, dispatches" },
  { title: "Carrier collects and delivers" },
  { title: "Customer receives confirmation + tracking" },
];

export default function DeliverySupport() {
  return (
    <>
      <Seo title="Delivery & support — EI Battery Solutions" description="Collection or Australia-wide delivery, contractor product support and warranty pathway for EI Battery Solutions wholesale customers." />
      <section className="relative bg-ei-navy text-ei-white overflow-hidden" data-testid="delivery-hero">
        <img
          src="/media/pages/delivery-dispatch.png"
          alt="EI Battery team member checking a dispatch of Dyness SBDU100 Pro cartons and a strapped Risen panel pallet loaded in a delivery van"
          className="absolute inset-0 w-full h-full object-cover object-right"
          data-testid="delivery-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ei-navy via-ei-navy/90 to-ei-navy/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-28">
          <Eyebrow className="text-ei-gold">Fulfilment &amp; support</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight">Delivery &amp; support</h1>
          <p className="mt-5 text-base md:text-lg text-ei-white/80 max-w-xl leading-relaxed">
            Collect from us or arrange Australia-wide delivery. We support your installation team through the product
            and warranty process.
          </p>
        </div>
      </section>

      <section id="order-flow" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 scroll-mt-24" data-testid="order-flow">
        <SectionHeading eyebrow="How an order runs" title="From order to delivery" />
        <p className="mt-4 text-sm text-ei-graphite/85 max-w-2xl leading-relaxed">
          Every wholesale order follows the same nine steps. Two steps are still being switched on and are marked
          below.
        </p>
        <ol className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ORDER_FLOW.map((step, i) => (
            <li
              key={step.title}
              data-testid={`order-flow-step-${String(i + 1).padStart(2, "0")}`}
              className={`relative bg-white border rounded-md p-6 transition-colors ${step.planned ? "border-dashed border-ei-gold/70" : "border-ei-mist hover:border-ei-gold"}`}
            >
              <div className="font-display text-2xl font-bold text-ei-gold/70 tnum">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-2 text-base font-semibold text-ei-navy">{step.title}</h3>
              {step.planned && (
                <span className="mt-3 inline-block text-xs font-semibold uppercase tracking-wide text-ei-golddark bg-ei-gold/10 border border-ei-gold/40 rounded-sm px-2 py-0.5" data-testid={`order-flow-planned-${String(i + 1).padStart(2, "0")}`}>
                  Coming soon
                </span>
              )}
            </li>
          ))}
        </ol>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { icon: PackageCheck, title: "Collection", body: "Once your order is assembled and payment is confirmed, we provide collection instructions." },
            { icon: Truck, title: "Australia-wide delivery", body: "Delivery to your nominated site is available for an additional freight charge. Freight rates are confirmed per order." },
            { icon: Clock, title: "Delivery estimates", body: "Typically around seven days. Standard orders up to 20 units can generally be fulfilled quickly; multi-pallet or container orders may take five to six weeks." },
            { icon: ShieldCheck, title: "Warranty pathway", body: "End customers contact their installer first. The contractor then contacts us, and we handle warranty claims directly with the contractor." },
          ].map((c) => (
            <div key={c.title} className="bg-white border border-ei-mist rounded-md p-6 sm:p-8">
              <c.icon className="w-8 h-8 text-ei-golddark" strokeWidth={1.5} />
              <h3 className="mt-5 text-lg font-semibold text-ei-navy">{c.title}</h3>
              <p className="mt-2 text-sm text-ei-graphite/85 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-ei-navy text-ei-white rounded-md p-8 lg:p-10">
          <SectionHeading eyebrow="Contractor support" title="We back your install team" />
          <p className="mt-4 text-ei-white/75 max-w-2xl leading-relaxed">
            EI Battery Solutions does not provide installation, system design or maintenance — those remain the
            contractor's responsibility. For product and warranty support, contact our team on{" "}
            <a href={COMPANY.phoneHref} className="text-ei-gold font-semibold">{COMPANY.phone}</a> or{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-ei-gold font-semibold">{COMPANY.email}</a>.
          </p>
        </div>

        <p className="mt-8 text-xs text-ei-graphite/60 max-w-3xl border-l-2 border-ei-gold/60 pl-4 leading-relaxed">
          Delivery times and order capacities shown are supplied estimates, not real-time stock or guarantees. Freight
          rates, payment timing and methods are confirmed with your quotation.
        </p>

        <div className="mt-14" data-testid="installer-resources">
          <SectionHeading eyebrow="Installer resources" title="Standards & technical guidance" />
          <p className="mt-4 text-sm text-ei-graphite/85 max-w-2xl leading-relaxed">
            Reference documents for your installation team. Design, installation and compliance remain the contractor's
            responsibility.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="/docs/risen-pv-module-install-maintenance-manual.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="resource-risen-install"
              className="group flex items-center justify-between bg-white border border-ei-mist rounded-md p-5 hover:border-ei-gold transition-colors"
            >
              <span className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-ei-golddark shrink-0" strokeWidth={1.5} />
                <span>
                  <span className="block text-sm font-semibold text-ei-navy">Risen PV module install &amp; maintenance manual</span>
                  <span className="block text-xs text-ei-graphite/60">Solar panel installation &amp; maintenance · PDF</span>
                </span>
              </span>
              <Download className="w-5 h-5 text-ei-graphite/50 group-hover:text-ei-golddark transition-colors" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
